import { useState, useRef } from "react";
import { Upload, RefreshCw, CheckCircle2, Loader2, Trash2, Image as ImageIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_EXT = ["jpg", "jpeg", "png", "heic", "heif", "webp"];
const MAX_SIZE = 25 * 1024 * 1024;

const SlotCard = ({ index, fathead, onUpload, onRemove, busy }) => {
  const fileRef = useRef(null);
  const proofSrc = fathead ? `${API}${fathead.proof_url}` : null;
  const pdfSrc = fathead ? `${API}${fathead.pdf_url}` : null;

  const pick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow same-file re-upload
    if (!file) return;
    await onUpload(index, file);
  };

  return (
    <div
      className="flex flex-col rounded-sm overflow-hidden"
      style={{ border: "2px solid var(--ups-ink)", background: "#fff" }}
      data-testid={`bighead-slot-${index}`}
    >
      <div
        className="overline px-3 py-2 flex items-center justify-between"
        style={{ background: "var(--ups-muted)", borderBottom: "2px solid var(--ups-ink)" }}
      >
        <span>Big Head #{index + 1}</span>
        {fathead && (
          <span className="flex items-center gap-1 text-[10px]" style={{ color: "#16A34A" }}>
            <CheckCircle2 size={12} /> Ready
          </span>
        )}
      </div>

      <div
        className="relative aspect-[4/3] flex items-center justify-center"
        style={{ background: "#F5F5F4" }}
      >
        {proofSrc ? (
          <img
            src={proofSrc}
            alt={`Big Head #${index + 1}`}
            className="w-full h-full object-contain"
            data-testid={`bighead-proof-${index}`}
          />
        ) : busy ? (
          <div className="flex flex-col items-center gap-2 text-sm" style={{ color: "var(--ups-subtext)" }}>
            <Loader2 className="animate-spin" size={24} />
            <span className="font-mono text-[11px]">Generating proof…</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={pick}
            className="flex flex-col items-center gap-2 px-4 py-6 text-sm hover:opacity-70 transition"
            style={{ color: "var(--ups-subtext)" }}
            data-testid={`bighead-upload-${index}`}
          >
            <Upload size={24} />
            <span className="font-mono text-[11px]">Click to upload photo</span>
            <span className="text-[10px]">JPG · PNG · HEIC · up to 25 MB</span>
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      <div className="p-2 flex items-center gap-2 text-[11px] font-mono">
        {fathead ? (
          <>
            <a
              href={pdfSrc}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 text-center py-2 px-2 rounded-sm hover:opacity-80 transition"
              style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}
              data-testid={`bighead-pdf-${index}`}
            >
              <ImageIcon size={12} className="inline mr-1" /> View PDF
            </a>
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="py-2 px-2 rounded-sm hover:opacity-80 transition"
              style={{ border: "1px solid var(--ups-border)" }}
              title="Replace photo"
              data-testid={`bighead-replace-${index}`}
            >
              <RefreshCw size={12} />
            </button>
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={busy}
              className="py-2 px-2 rounded-sm hover:opacity-80 transition"
              style={{ border: "1px solid var(--ups-border)" }}
              title="Remove"
              data-testid={`bighead-remove-${index}`}
            >
              <Trash2 size={12} />
            </button>
          </>
        ) : (
          <span style={{ color: "var(--ups-subtext)" }}>Upload to generate instant proof.</span>
        )}
      </div>
    </div>
  );
};

const BigHeadsPanel = ({ sessionId, quantity, fatheads, onChange }) => {
  const [busyIdx, setBusyIdx] = useState(null);
  const [error, setError] = useState("");

  const slots = Array.from({ length: Math.max(1, quantity || 1) }, (_, i) => fatheads[i] || null);
  const readyCount = slots.filter(Boolean).length;

  const validate = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) return "Unsupported file type — JPG, PNG, HEIC, WebP.";
    if (file.size > MAX_SIZE) return "Image too large (max 25 MB).";
    return null;
  };

  const handleUpload = async (idx, file) => {
    const err = validate(file);
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setBusyIdx(idx);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId);
      fd.append("file", file);
      const res = await axios.post(`${API}/fathead/generate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const next = [...fatheads];
      next[idx] = res.data;
      onChange(next);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not generate the Big Head. Try a different photo.";
      setError(msg);
    } finally {
      setBusyIdx(null);
    }
  };

  const handleRemove = async (idx) => {
    const fh = fatheads[idx];
    if (!fh) return;
    const next = fatheads.slice(0, idx).concat(fatheads.slice(idx + 1));
    onChange(next);
    try {
      await axios.delete(`${API}/fathead/${fh.fathead_id}`);
    } catch {
      /* silent */
    }
  };

  return (
    <div
      className="mt-5 mb-5 p-4 rounded-sm"
      style={{ background: "#FFFBEB", border: "2px dashed var(--ups-ink)" }}
      data-testid="bigheads-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="overline mb-1" style={{ color: "var(--ups-ink)" }}>
            Big Head Artwork · Upload {quantity} photo{quantity === 1 ? "" : "s"}
          </div>
          <div className="text-[12px] leading-snug" style={{ color: "var(--ups-subtext)" }}>
            We&apos;ll automatically remove the background, keep the face (with hair and accessories),
            and wrap it with a 0.125&quot; black outline on a 24 × 18 in board. The original photo and
            the generated PDF are attached to your order email.
          </div>
        </div>
        <div
          className="font-mono text-xs whitespace-nowrap px-2 py-1"
          style={{ background: readyCount === quantity ? "#16A34A" : "var(--ups-muted)", color: readyCount === quantity ? "#fff" : "var(--ups-ink)", border: "1px solid var(--ups-ink)" }}
          data-testid="bigheads-ready-count"
        >
          {readyCount} / {quantity} ready
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {slots.map((fh, i) => (
          <SlotCard
            key={i}
            index={i}
            fathead={fh}
            onUpload={handleUpload}
            onRemove={handleRemove}
            busy={busyIdx === i}
          />
        ))}
      </div>

      {error && (
        <div
          className="mt-3 text-[12px] font-mono px-3 py-2"
          style={{ background: "#FEE2E2", border: "1px solid #DC2626", color: "#991B1B" }}
          data-testid="bigheads-error"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default BigHeadsPanel;
